function tryParseCoordinate(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value !== 'string') return null
  const normalized = value.trim().replace(/,/g, '')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizedName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

const GEO_SAMPLE_SIZE = 500

function getCoordinateStats(rows, column) {
  const parsedValues = rows
    .map((row) => tryParseCoordinate(row?.[column]))
    .filter((value) => value !== null)

  if (!parsedValues.length) {
    return {
      validCount: 0,
      totalCount: rows.length,
      range: null,
      validLatitudeRatio: 0,
      validLongitudeRatio: 0,
    }
  }

  const validLatitudeCount = parsedValues.filter(
    (value) => value >= -90 && value <= 90
  ).length
  const validLongitudeCount = parsedValues.filter(
    (value) => value >= -180 && value <= 180
  ).length

  let minVal = parsedValues[0]
  let maxVal = parsedValues[0]
  for (let i = 1; i < parsedValues.length; i++) {
    if (parsedValues[i] < minVal) minVal = parsedValues[i]
    if (parsedValues[i] > maxVal) maxVal = parsedValues[i]
  }

  return {
    validCount: parsedValues.length,
    totalCount: rows.length,
    range: { min: minVal, max: maxVal },
    validLatitudeRatio: validLatitudeCount / parsedValues.length,
    validLongitudeRatio: validLongitudeCount / parsedValues.length,
  }
}

function scoreLatitude(name, stats) {
  let score = 0
  if (/^(lat|latitude|ylat)$/.test(name) || name.includes('latitude')) score += 3
  if (name.includes('lat')) score += 2
  if (stats.validLatitudeRatio >= 0.95) score += 2
  else if (stats.validLatitudeRatio >= 0.8) score += 1
  if (stats.validLongitudeRatio < stats.validLatitudeRatio) score += 1
  return score
}

function scoreLongitude(name, stats) {
  let score = 0
  if (
    /^(lon|lng|long|longitude|xlon)$/.test(name) ||
    name.includes('longitude')
  ) {
    score += 3
  }
  if (name.includes('lon') || name.includes('lng') || name.includes('long')) {
    score += 2
  }
  if (stats.validLongitudeRatio >= 0.95) score += 2
  else if (stats.validLongitudeRatio >= 0.8) score += 1
  if (stats.validLongitudeRatio > stats.validLatitudeRatio) score += 1
  return score
}

function pickBestCandidate(candidates) {
  return [...candidates]
    .sort((a, b) => b.score - a.score)
    .find((candidate) => candidate.score >= 4 && candidate.stats.validCount >= 2) || null
}

export function detectGeoColumns(rows = []) {
  const sampleRows = rows.length > GEO_SAMPLE_SIZE
    ? rows.filter((_, i) => i % Math.ceil(rows.length / GEO_SAMPLE_SIZE) === 0)
    : rows
  const columns = sampleRows[0] ? Object.keys(sampleRows[0]) : []
  const candidates = columns.map((column) => {
    const name = normalizedName(column)
    const stats = getCoordinateStats(sampleRows, column)
    return {
      column,
      stats,
      latitudeScore: scoreLatitude(name, stats),
      longitudeScore: scoreLongitude(name, stats),
    }
  })

  const latitude = pickBestCandidate(
    candidates.map((candidate) => ({
      column: candidate.column,
      score: candidate.latitudeScore,
      stats: candidate.stats,
    }))
  )
  const longitude = pickBestCandidate(
    candidates.map((candidate) => ({
      column: candidate.column,
      score: candidate.longitudeScore,
      stats: candidate.stats,
    }))
  )

  const warnings = []
  if (latitude?.column && latitude.stats.validLatitudeRatio < 1) {
    warnings.push(
      `${latitude.column}: includes values outside the usual latitude range of -90 to 90.`
    )
  }
  if (longitude?.column && longitude.stats.validLongitudeRatio < 1) {
    warnings.push(
      `${longitude.column}: includes values outside the usual longitude range of -180 to 180.`
    )
  }

  if (
    latitude?.column &&
    longitude?.column &&
    latitude.column !== longitude.column &&
    latitude.stats.validLatitudeRatio < longitude.stats.validLatitudeRatio &&
    longitude.stats.validLongitudeRatio < latitude.stats.validLatitudeRatio
  ) {
    warnings.push(
      'Latitude and longitude may be swapped. Double-check the coordinate columns before mapping them.'
    )
  }

  return {
    latitude: latitude
      ? {
          column: latitude.column,
          confidence: latitude.score,
          stats: latitude.stats,
        }
      : null,
    longitude: longitude
      ? {
          column: longitude.column,
          confidence: longitude.score,
          stats: longitude.stats,
        }
      : null,
    warnings,
  }
}
